/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, computed, effect, forwardRef, input, output, signal, untracked } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";
import { ColorPickerDirective } from "ngx-color-picker";

import { AdaaHelper } from "../../../../core/utils";
import { Language, PageMode } from "../../../constants/enums";
import { ParameterCatalog } from "../../../models";

@Component({
  selector: "adaa-form-input-color",
  imports: [TranslatePipe, ColorPickerDirective],
  templateUrl: "./form-input-color.html",
  styleUrl: "./form-input-color.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormInputColor),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormInputColor),
    },
  ],
})
export class FormInputColor implements ControlValueAccessor {
  readonly inputControl = new FormControl();

  private readonly _requiredFromValidator = signal<boolean>(false);
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  private _onChange: (value: string) => void;
  private _disabledByInput = false;

  readonly PageMode = PageMode;
  readonly showAsRequired = input<boolean>(false);
  readonly required = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly classes = input<string>("form-control shadow-sm");
  readonly textAlignment = input<"default" | "left" | "right" | "center">(
    AdaaHelper.getCurrentLang() === Language.Arabic ? "right" : "left"
  );
  readonly direction = input<string>("");
  readonly color = input<string>("#000");
  readonly pageMode = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly setDefaultValue = input<unknown>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  readonly inputChanges = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());

  _color: string = "#000";

  constructor() {
    effect(() => {
      this._color = this.color();
    });

    effect(() => {
      if (this.isDisabled()) {
        this._disabledByInput = true;
        this.inputControl.disable();
      } else if (this._disabledByInput) {
        this._disabledByInput = false;
        this.inputControl.enable();
      }
    });

    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      untracked(() => {
        if (value) {
          this.writeValue(value as string);
        } else {
          this.inputControl.reset();
        }
      });
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          untracked(() => this._requiredFromValidator.set(field.mandatory === "Y"));
        }
      }
    });
  }

  public writeValue(obj: string | number): void {
    if (obj === null) {
      this.inputControl.reset();
    }

    this.inputControl.setValue(obj);
  }

  public registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(_value: FormControl): false | { required: boolean } {
    const isRequired =
      (this.isRequired() && this.inputControl.value === null) || (this.isRequired() && this.inputControl.value === "");
    return (
      isRequired && {
        required: true,
      }
    );
  }

  public onChange(event: any): void {
    this.inputControl.setValue(event);
    this.valueChanged(event);
  }

  public valueChanged(event: any): void {
    if (this._onChange) this._onChange(event);

    this.writeValue(event);
    this.inputChanges.emit(event);
  }
}
